import { useMemo, useState } from "react";
import { ChevronRight } from "lucide-react";
import Submenu from "../../components/Menu/Submenu";
import { useMenuConfig } from "./hooks/useMenuConfig";
import { useMenuContext } from "../../hooks/context/useMenuContext";
import { cn } from "../../../../../lib/utils";

function MenuContent({
    openActionsModal,
    openCopyModal,
    openDeleteModal,
    openAssignModal,
    openProfileSheet,
    openTransferModal,
    openShiftTradeDrawer,
    openClaimShiftDrawer,
    openSaveDayAsTemplate,
    openLoadTemplatesModal,
    openMoveToOpenShiftsModal
}: any) {
    const config = useMenuConfig({
        openActionsModal,
        openCopyModal,
        openDeleteModal,
        openAssignModal,
        openProfileSheet,
        openTransferModal,
        openShiftTradeDrawer,
        openClaimShiftDrawer,
        openSaveDayAsTemplate,
        openLoadTemplatesModal,
        openMoveToOpenShiftsModal
    });

    const { id, x, closeMenu } = useMenuContext();
    const [openSubmenu, setOpenSubmenu] = useState<string | null>(null);

    const submenuDirection = useMemo(() => {
        if (typeof window === "undefined") return "right";
        const viewportWidth = document.documentElement.clientWidth;
        return (x || 0) > viewportWidth / 2 ? "left" : "right";
    }, [x]);

    const content = useMemo(() => {
        if (!id) return null;

        const menuConfig = (config as any)[id];
        if (!menuConfig) return null;

        return menuConfig.map((item: any, i: number) => {
            const Icon = item.icon;
            const hasSubmenu = !!item?.submenu;
            const isOpen = openSubmenu === item.submenu?.key;

            return (
                <div
                    key={`${id}-${i}`}
                    className="tw-relative tw-w-full"
                >
                    <button
                        className={cn(
                            "context-menu-item tw-flex tw-items-center tw-gap-2 tw-justify-between tw-w-full tw-text-left tw-px-2 tw-py-1.5 hover:tw-bg-neutral-100 tw-rounded-sm tw-text-sm tw-transition-colors",
                            item.hidden && "tw-hidden",
                            item.disabled && "tw-opacity-50 tw-cursor-not-allowed"
                        )}
                        onClick={(e) => {
                            e.stopPropagation();
                            if (hasSubmenu) {
                                setOpenSubmenu(item.submenu.key);
                            } else {
                                item.onChange();
                                closeMenu();
                            }
                        }}
                        onMouseEnter={() => {
                            if (hasSubmenu) {
                                setOpenSubmenu(item.submenu.key);
                            } else {
                                setOpenSubmenu(null);
                            }
                        }}
                        disabled={item.disabled}
                    >
                        <span className="tw-flex tw-items-center tw-gap-2">
                            {Icon && <Icon className="tw-w-4 tw-h-4" />} {item.label}
                        </span>

                        {hasSubmenu && <ChevronRight size={14} />}
                    </button>

                    {(hasSubmenu && isOpen) && (
                        <Submenu
                            direction={submenuDirection}
                            className="tw-z-50"
                            onMouseLeave={() => setOpenSubmenu(null)}
                        >
                            {item.submenu.content}
                        </Submenu>
                    )}
                </div>
            );
        });
    }, [id, config, openSubmenu, submenuDirection, closeMenu]);

    if ((config as any)?.[id]?.length === 0) {
        return "no-content";
    }

    return content;
}

export default MenuContent;